import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, asItems } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const ShortlistContext = createContext(null);

export function ShortlistProvider({ children }) {
  const { user } = useAuth();
  const [savedIds, setSavedIds] = useState([]);

  const loadShortlist = useCallback(async () => {
    if (!user) {
      setSavedIds([]);
      return;
    }
    try {
      const { data } = await api.get("/saved");
      const items = asItems(data);
      const ids = items.map(p => p.property_id || p.id).filter(Boolean);
      setSavedIds(ids);
    } catch (e) {
      console.error("Failed to load shortlist", e);
    }
  }, [user]);

  useEffect(() => {
    loadShortlist();
  }, [loadShortlist]);

  const isSaved = (id) => savedIds.includes(id);

  const toggle = async (id) => {
    if (!user) {
      toast.error("Please log in to save properties");
      return;
    }
    const alreadySaved = isSaved(id);
    if (alreadySaved) {
      setSavedIds(prev => prev.filter(x => x !== id));
      try {
        await api.delete(`/saved/${id}`);
        toast.success("Removed from shortlist");
      } catch (e) {
        setSavedIds(prev => [...prev, id]);
        toast.error("Failed to remove from shortlist");
      }
    } else {
      setSavedIds(prev => [...prev, id]);
      try {
        await api.post("/saved", { property_id: id });
        toast.success("Saved to shortlist");
      } catch (e) {
        setSavedIds(prev => prev.filter(x => x !== id));
        toast.error("Failed to save to shortlist");
      }
    }
  };

  return (
    <ShortlistContext.Provider value={{ savedIds, isSaved, toggle, reload: loadShortlist }}>
      {children}
    </ShortlistContext.Provider>
  );
}

export const useShortlist = () => useContext(ShortlistContext);
